/**
 * Clear-contours proposal card (the runtime HITL gate) for the one agent
 * operation that edits an existing structure IN PLACE. Pure presentational:
 * it shows the RESOLVED target and exactly what will be cleared — for the
 * current-slice scope, the slice position captured when the card was built
 * (Apply clears that slice even if the view has moved since). There is no
 * geometry preview; the note states that the structure itself changes and
 * that Undo restores it. Frame, status chip and Apply/Reject live in
 * {@link ProposalCardShell}.
 */
import { Typography } from '@mui/material';
import type { ClearScope } from '~/agent';
import { formatSignedCm, mmToCm } from '~/utils/units';
import ProposalCardShell, {
    ProposalSummaryBox,
    TargetLine,
    type ProposalStatus,
} from './ProposalCardShell';

export interface ClearContoursProposalCardProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    readonly currentVolumeCm3: number;
    readonly scope: ClearScope;
    readonly keepEveryNth?: number;
    /** Set for the current-slice scope (mm, patient z). */
    readonly slicePositionMm?: number;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

function describeScope(
    scope: ClearScope,
    keepEveryNth: number | undefined,
    slicePositionMm: number | undefined
): string {
    switch (scope) {
        case 'all':
            return 'clear ALL slices';
        case 'keep-every-nth':
            return (
                `keep 1 in every ${keepEveryNth} slices (plus the first ` +
                'and last drawn slice), clear the rest'
            );
        case 'current-slice':
            return (
                'clear the slice shown in the main view (z = ' +
                `${formatSignedCm(mmToCm(slicePositionMm ?? Number.NaN))})`
            );
    }
}

export default function ClearContoursProposalCard({
    targetName,
    targetRoiNumber,
    currentVolumeCm3,
    scope,
    keepEveryNth,
    slicePositionMm,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: ClearContoursProposalCardProps) {
    return (
        <ProposalCardShell
            title="Clear contours"
            ariaLabel="Clear contours proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <TargetLine
                targetName={targetName}
                targetRoiNumber={targetRoiNumber}
                currentVolumeCm3={currentVolumeCm3}
            />

            <ProposalSummaryBox>
                {describeScope(scope, keepEveryNth, slicePositionMm)} of “
                {targetName}”
            </ProposalSummaryBox>

            <Typography variant="caption" color="text.secondary">
                This edits “{targetName}” itself; no new structure is created.
                Undo restores it.
            </Typography>
        </ProposalCardShell>
    );
}
